// Tiny pub/sub bridging the global access-token provider (which is what triggers
// Auth0 silent refreshes during real activity) and the session monitor that
// schedules an expiry logout. Whenever a token is observed — fetched fresh or
// refreshed — subscribers re-arm against its `exp`, so an active user's logout
// is pushed back while an idle one (no API calls → no refresh) is logged out
// when the current token expires.

type Listener = (expMs: number | null) => void;

const listeners = new Set<Listener>();

/** Decode a JWT's `exp` claim and return it in epoch milliseconds, or null. */
export function decodeJwtExpMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    );
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Notify subscribers that an access token was just observed (fetched/refreshed). */
export function notifyTokenObserved(token: string | null): void {
  const expMs = token ? decodeJwtExpMs(token) : null;
  listeners.forEach((l) => l(expMs));
}

/** Subscribe to token observations. Returns an unsubscribe function. */
export function onTokenObserved(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// --- Forced-logout channel ----------------------------------------------------
// Signals that the session is no longer recoverable (e.g. the token endpoint
// rejected a refresh) and the user must be logged out.

type VoidListener = () => void;

const invalidListeners = new Set<VoidListener>();

/** Notify subscribers that the session is invalid and a logout is required. */
export function notifySessionInvalid(): void {
  invalidListeners.forEach((l) => l());
}

/** Subscribe to forced-logout signals. Returns an unsubscribe function. */
export function onSessionInvalid(listener: VoidListener): () => void {
  invalidListeners.add(listener);
  return () => { invalidListeners.delete(listener); };
}

// Auth0's silent refresh hits POST /oauth/token via its own fetch (not axios),
// and the SDK discards the HTTP status before throwing — so the only reliable
// way to react to a 403 there is to watch fetch directly.
let tokenEndpointWatcherInstalled = false;

/**
 * Install a one-time fetch wrapper that fires {@link notifySessionInvalid} when
 * a POST to the OAuth token endpoint returns 403. Reading `response.status`
 * does not consume the body, so the original response is returned untouched.
 */
export function installTokenEndpointWatcher(): void {
  if (tokenEndpointWatcherInstalled) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  tokenEndpointWatcherInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args);
    try {
      const [input, init] = args;
      const url =
        typeof input === 'string' ? input
        : input instanceof URL ? input.href
        : (input as Request).url;
      const method = String(
        init?.method ?? (input instanceof Request ? input.method : 'GET')
      ).toUpperCase();

      if (response.status === 403 && method === 'POST' && url.includes('/oauth/token')) {
        notifySessionInvalid();
      }
    } catch {
      // Never let watcher logic break fetch.
    }
    return response;
  };
}
